/**
 * The publish confirmation — §7.4 and [ADR 0004](../../../../../docs/adr/0004-publish-moves-not-clones.md).
 *
 * §7.4 does not ask for a confirmation, it asks for "a confirmation **naming the
 * audience**". A dialog that says "Publish this document?" and nothing else
 * satisfies the sentence and fails the requirement, so this renders every field
 * the plan carries and leads with the two that are the point: who gains access
 * and who loses it.
 *
 * The plan is not computed here and could not be. `GET /api/documents/:id/publish`
 * produces it by running the real publish inside a transaction and rolling it
 * back, precisely so the dialog and the outcome are the same code — ADR 0004
 * calls a cheaper preview "a correctness obligation on a code path that only
 * runs occasionally, the kind that rots quietly". Every number below is
 * therefore the server's, unmodified. The one thing this file adds is emphasis.
 *
 * §4.6's mitigation rides along: "placing or publishing a link into a client
 * space requires confirmation". Links are outside the ACL — a linked Google Doc
 * is exactly as restricted as Google decided it is — so any link travelling with
 * the node gets named, and gets named loudly when the destination is a client
 * space.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { usePublishDocument, usePublishPreview } from '../../api/queries.ts';
import type {
  AudienceMember,
  DocumentView,
  PublishPlan,
  PublishResponse,
} from '../../api/types.ts';
import { Badge, EffectBadge, RoleBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';
import { Mono } from '../../components/ui/layout.tsx';
import { SpaceKindBadge } from './vocabulary.tsx';

function Section({
  title,
  tone = 'plain',
  children,
}: {
  title: string;
  tone?: 'plain' | 'warning';
  children: ReactNode;
}) {
  return (
    <section
      className={
        tone === 'warning'
          ? 'rounded-lg border border-warning bg-warning-primary px-4 py-3'
          : 'rounded-lg border border-secondary px-4 py-3'
      }
    >
      <h3 className="text-xs font-semibold tracking-wide text-tertiary uppercase">{title}</h3>
      <div className="mt-2 flex flex-col gap-2 text-sm text-secondary">{children}</div>
    </section>
  );
}

function PersonLine({ person }: { person: AudienceMember }) {
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="font-medium text-primary">{person.name}</span>
      <span className="text-xs text-tertiary">{person.email}</span>
      <RoleBadge role={person.role} />
    </li>
  );
}

/**
 * Who gains and who loses. The first thing in the dialog, above the mechanics.
 *
 * An empty `gains` **and** an empty `loses` is stated rather than omitted: "this
 * changes nobody's access" is a real and reassuring answer, and a section that
 * silently disappears reads as a section that failed to load.
 */
function Audience({ plan }: { plan: PublishPlan }) {
  const { gains, loses, after } = plan.audience;
  const [showAll, setShowAll] = useState(false);

  return (
    <Section title="Audience" tone={gains.length > 0 || loses.length > 0 ? 'warning' : 'plain'}>
      {gains.length === 0 && loses.length === 0 ? (
        <p>
          Nobody gains or loses access. The {after.length}{' '}
          {after.length === 1 ? 'person' : 'people'} who can read this can already read it.
        </p>
      ) : null}

      {gains.length > 0 ? (
        <div>
          <p className="font-semibold text-primary">
            {gains.length} {gains.length === 1 ? 'person gains' : 'people gain'} access
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {gains.map((person) => (
              <PersonLine key={person.personId} person={person} />
            ))}
          </ul>
        </div>
      ) : null}

      {loses.length > 0 ? (
        <div>
          <p className="font-semibold text-primary">
            {loses.length} {loses.length === 1 ? 'person loses' : 'people lose'} access
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {loses.map((person) => (
              <PersonLine key={person.personId} person={person} />
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <Button size="sm" variant="tertiary" onPress={() => setShowAll((open) => !open)}>
          {showAll ? 'Hide' : 'Show'} everyone who can read it afterwards ({after.length})
        </Button>
        {showAll ? (
          <ul className="mt-2 flex flex-col gap-1">
            {after.map((person) => (
              <PersonLine key={person.personId} person={person} />
            ))}
          </ul>
        ) : null}
      </div>
    </Section>
  );
}

/**
 * §7.6's gate, and the reason the primary button can be disabled.
 *
 * The distinction between required and advisory is the whole reason §7.6 makes
 * `requirement` a column — "legal should weigh in but isn't a gate" — so the two
 * sets are rendered apart and labelled with what they mean, never merged into
 * one "waiting on" list that would send the author chasing a signature that was
 * never going to block them.
 *
 * The cleared case says so rather than rendering nothing. An approval that
 * passes silently is indistinguishable from an approval step that is not wired
 * up, and the person pressing publish on a policy revision is entitled to see
 * which reading applies.
 */
function Approval({ plan }: { plan: PublishPlan }) {
  const { approval } = plan;
  const { outstandingRequired, outstandingAdvisory } = approval;

  return (
    <Section title="Editorial approval" tone={approval.mayPublish ? 'plain' : 'warning'}>
      <p className={approval.mayPublish ? undefined : 'font-semibold text-primary'}>
        {approval.explanation}
      </p>

      {outstandingRequired.length > 0 ? (
        <div>
          <p className="font-semibold text-primary">
            Required — publishing waits on {outstandingRequired.length === 1 ? 'this' : 'these'}
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {outstandingRequired.map((reviewer) => (
              <li key={reviewer.personId} className="text-primary">
                {reviewer.name ?? <Mono>{reviewer.personId}</Mono>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {outstandingAdvisory.length > 0 ? (
        <div>
          <p>
            Advisory, and not a gate (§7.6) — publishing does not wait on{' '}
            {outstandingAdvisory.length === 1 ? 'this reviewer' : 'these reviewers'}:
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {outstandingAdvisory.map((reviewer) => (
              <li key={reviewer.personId} className="text-secondary">
                {reviewer.name ?? <Mono>{reviewer.personId}</Mono>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  );
}

/** §4.6 — the one thing in this system whose access nothing here can enforce. */
function ExternalLinks({ plan }: { plan: PublishPlan }) {
  if (plan.externalLinks.length === 0) return null;
  const intoClientSpace = plan.to.spaceKind === 'client';

  return (
    <Section title="External links travelling with this" tone="warning">
      <p>
        {plan.externalLinks.length} {plan.externalLinks.length === 1 ? 'link moves' : 'links move'}{' '}
        with this document
        {intoClientSpace ? ' into a client space' : ''}. Permissions on a link cannot be enforced —
        a linked Google Doc is exactly as restricted as Google decided it is, and the risk runs
        toward a target shared more widely than intended.
      </p>
      <ul className="flex flex-col gap-1">
        {plan.externalLinks.map((link) => (
          <li key={link.nodeId} className="flex flex-col">
            <span className="flex items-center gap-1.5 font-medium text-primary">
              {link.title}
              <Badge tone="outline">
                <span aria-hidden="true">↗</span> external
              </Badge>
            </span>
            <span className="font-mono text-xs break-all text-tertiary">{link.url}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Destination({ plan }: { plan: PublishPlan }) {
  return (
    <Section title="Destination" tone={plan.to.spaceKind === 'client' ? 'warning' : 'plain'}>
      <p className="flex flex-wrap items-center gap-2">
        <span>{plan.from.spaceName}</span>
        <SpaceKindBadge kind={plan.from.spaceKind} />
        <span aria-hidden="true">→</span>
        <span className="font-semibold text-primary">{plan.to.spaceName}</span>
        <SpaceKindBadge kind={plan.to.spaceKind} />
      </p>
      {plan.alreadyInDestination ? (
        <p>
          The node is already where publishing puts it, so nothing moves — this republishes in place
          and repoints what readers resolve to.
        </p>
      ) : (
        <p>
          Publishing <span className="font-semibold">moves</span> the node rather than copying it
          (ADR 0004): one document, one identity, one version history across the boundary.
        </p>
      )}
      {plan.to.spaceKind === 'client' ? (
        <p className="font-semibold text-primary">
          This lands in a client space. §4.3 renders a shared space wholesale — being in it is what
          makes it visible to the client, not a per-document flag.
        </p>
      ) : null}
    </Section>
  );
}

function Mechanics({ plan }: { plan: PublishPlan }) {
  const travelling = plan.moving.filter((node) => node.nodeId !== plan.nodeId);

  return (
    <Section title="What else happens">
      <p>
        {plan.willSnapshot
          ? 'The working draft is snapshotted as a new version, and that version becomes the one readers resolve to.'
          : 'There is no draft to snapshot; the current published version stays the published version.'}
      </p>
      {travelling.length > 0 ? (
        <div>
          <p>
            {travelling.length} child {travelling.length === 1 ? 'node travels' : 'nodes travel'}{' '}
            with it — a descendant cannot be left behind, because a child node must live in its
            parent&apos;s space.
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {travelling.map((node) => (
              <li key={node.nodeId} className="flex flex-wrap items-center gap-2">
                <span className="text-primary">{node.title}</span>
                <Badge tone="outline">{node.type}</Badge>
                {node.deleted ? <Badge tone="gray">deleted</Badge> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {plan.nodeOverrides.length > 0 ? (
        <div>
          <p>
            {plan.nodeOverrides.length} node-level{' '}
            {plan.nodeOverrides.length === 1 ? 'grant is' : 'grants are'} re-evaluated against the
            new ancestry. The grants do not move; their meaning does.
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {plan.nodeOverrides.map((override) => (
              <li key={override.grantId} className="flex flex-wrap items-center gap-2">
                <Badge tone="outline" mono>
                  {override.capability}
                </Badge>
                <EffectBadge effect={override.effect} />
                <span className="text-xs text-tertiary">
                  {override.principalType} <Mono>{override.principalId}</Mono>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * §6.4 — publishing past `document.publish`.
 *
 * Behind a disclosure and behind a mandatory reason, because that is what makes
 * it self-documenting six months later. It is authorized as `admin.override` and
 * recorded as `admin.force_publish`, never as `document.published` — an
 * override that logs as the ordinary action is an override nobody can find.
 */
function ForceOverride({
  force,
  setForce,
  reason,
  setReason,
}: {
  force: boolean;
  setForce: (value: boolean) => void;
  reason: string;
  setReason: (value: string) => void;
}) {
  if (!force) {
    return (
      <Button size="sm" variant="tertiary" onPress={() => setForce(true)}>
        Publish as an administrative override
      </Button>
    );
  }

  return (
    <Alert tone="warning" title="This is an administrative override">
      <div className="mt-2 flex flex-col gap-3">
        <p>
          Authorized against <span className="font-mono">admin.override</span> rather than{' '}
          <span className="font-mono">document.publish</span>, and recorded as{' '}
          <span className="font-mono">admin.force_publish</span> with this reason attached.
        </p>
        <TextField
          label="Reason"
          value={reason}
          onChange={setReason}
          multiline
          rows={2}
          isRequired
        />
        <div>
          <Button size="sm" variant="tertiary" onPress={() => setForce(false)}>
            Publish normally instead
          </Button>
        </div>
      </div>
    </Alert>
  );
}

export interface PublishDialogProps {
  document: DocumentView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPublished?: (result: PublishResponse) => void;
}

export function PublishDialog({ document, isOpen, onOpenChange, onPublished }: PublishDialogProps) {
  return (
    <Dialog
      title="Publish"
      description={document.title}
      size="lg"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <PublishConfirmation
          document={document}
          onClose={close}
          {...(onPublished ? { onPublished } : {})}
        />
      )}
    </Dialog>
  );
}

function PublishConfirmation({
  document,
  onClose,
  onPublished,
}: {
  document: DocumentView;
  onClose: () => void;
  onPublished?: (result: PublishResponse) => void;
}) {
  // The dialog is already open by the time this mounts, so the preview is
  // fetched unconditionally. It carries `staleTime: 0` and `gcTime: 0` in
  // `queries.ts`: a cached audience is an audience from before the last grant.
  const preview = usePublishPreview(document.nodeId);
  const publish = usePublishDocument(document.nodeId);
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');

  const plan = preview.data?.confirmation;
  const reasonMissing = force && reason.trim() === '';

  return (
    <>
      <DialogBody>
        {preview.isPending ? <Loading label="Working out who this reaches…" /> : null}
        {preview.isError ? <ErrorState error={preview.error} /> : null}

        {plan ? (
          <>
            <Destination plan={plan} />
            <Approval plan={plan} />
            <Audience plan={plan} />
            <ExternalLinks plan={plan} />
            <Mechanics plan={plan} />
            <ForceOverride
              force={force}
              setForce={setForce}
              reason={reason}
              setReason={setReason}
            />
          </>
        ) : null}

        {publish.isError ? <ErrorState error={publish.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={publish.isPending}
          // No plan means no confirmation was shown, and `confirmed: true` would
          // then be a claim this screen cannot make (§7.4).
          //
          // The approval gate disables it too, and only the override reopens it.
          // The server refuses either way — this check is not the enforcement,
          // and moving it here would be the reimplementation §4.7 warns about —
          // but a button that is live and then 409s teaches people the dialog is
          // lying to them, which is the credibility the confirmation runs on.
          isDisabled={!plan || reasonMissing || (!plan.approval.mayPublish && !force)}
          onPress={() => {
            if (!plan) return;
            publish.mutate(
              {
                confirmed: true,
                ...(force ? { force: true, reason: reason.trim() } : {}),
              },
              {
                onSuccess: (result) => {
                  onPublished?.(result);
                  onClose();
                },
              },
            );
          }}
        >
          {plan ? `Publish to ${plan.to.spaceName}` : 'Publish'}
        </Button>
      </DialogFooter>
    </>
  );
}

/** What just happened, for the page to render after the dialog closes. */
export function PublishedNotice({ result }: { result: PublishResponse }) {
  const { published } = result;
  return (
    <Alert
      tone="success"
      title={`Published as version ${published.versionNumber} into ${published.to.spaceName}`}
    >
      <p>
        {published.audience.gains.length} gained access, {published.audience.loses.length} lost it,
        and {published.moved.length} {published.moved.length === 1 ? 'node' : 'nodes'} moved.
        {published.externalLinks.length > 0
          ? ` ${published.externalLinks.length} external link(s) travelled with it.`
          : ''}
      </p>
    </Alert>
  );
}
